import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, ExternalLink, Calendar, User, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { formatDate, stripHTML } from '../utils/rssParser';

/**
 * ArticleModal - Displays article content in a modal for inline reading
 */
const ArticleModal = ({ isOpen, onClose, article }) => {
  if (!article) return null;

  // Sanitize and prepare content
  const sanitizeContent = (content) => {
    if (!content) return '';
    // Allow images and basic formatting, remove scripts
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'strong', 'em', 'u', 's', 'del', 'ins',
        'ul', 'ol', 'li',
        'a', 'img',
        'blockquote', 'pre', 'code',
        'div', 'span'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style', 'target']
    });
  };

  // Check if content is markdown
  const isMarkdown = (text) => {
    if (!text) return false;
    const markdownIndicators = ['#', '**', '*', '- ', '1.', '```', '>'];
    return markdownIndicators.some(indicator => text.includes(indicator));
  };

  const renderContent = () => {
    const content = article.content || article.description || '';
    
    if (!content.trim()) {
      return (
        <p className="text-gray-500 italic">
          No content available for this article.
        </p>
      );
    }

    if (isMarkdown(content)) {
      return <ReactMarkdown className="prose prose-invert max-w-none">{content}</ReactMarkdown>;
    }

    return (
      <div 
        className="prose prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: sanitizeContent(content) }}
      />
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gray-900 border border-gray-700 shadow-2xl transition-all">
                {/* Header Image */}
                {article.image && (
                  <div className="relative h-64 md:h-80 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" />
                  </div>
                )}

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors z-10"
                >
                  <X className="w-5 h-5 text-white" />
                </button>

                {/* Content */}
                <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto">
                  {/* Title */}
                  <Dialog.Title as="h2" className="text-2xl md:text-3xl font-bold text-white mb-4">
                    {article.title}
                  </Dialog.Title>

                  {/* Meta Information */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-6">
                    {article.author && (
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{article.author}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(article.pubDate)}</span>
                    </div>
                    {article.categories && article.categories.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap">
                        <Tag className="w-4 h-4" />
                        {article.categories.slice(0, 3).map((cat, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-purple-600/30 text-purple-300 rounded-full text-xs"
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Description/Snippet */}
                  {article.description && (
                    <p className="text-gray-300 text-lg mb-6 leading-relaxed border-l-4 border-purple-500 pl-4">
                      {stripHTML(article.description)}
                    </p>
                  )}

                  {/* Main Content */}
                  <div className="text-gray-300 leading-relaxed">
                    {renderContent()}
                  </div>
                </div>

                {/* Footer with Action Buttons */}
                <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700 flex items-center justify-between">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  >
                    Close
                  </button>
                  {article.link && (
                    <a
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                      <span>Read Original</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ArticleModal;
